<script setup lang="ts">
import { Feature } from 'ol'
import { Geometry } from 'ol/geom'
import Stroke from 'ol/style/Stroke'
import Fill from 'ol/style/Fill'
import Style from 'ol/style/Style'
import cloneDeep from 'lodash/cloneDeep'

import chroma, { Color, Scale } from 'chroma-js'

import BaseLayerVector from '@/components/map/base-layers/BaseLayerVector.vue'
import { useMapStore } from '~/stores/map'
import { LayerLegend } from '~/types/map'

const vectorStyle = new Style({})

const mapStore = useMapStore()
const heatmapSettings = computed(() => mapStore.heatmapSettings)

const heatmapData = computed(() =>
  heatmapSettings.value.areaType === 'municipality'
    ? mapStore.heatmapMunicipalityData
    : mapStore.heatmapCountyData
)
const heatmapDataIsReady = computed(() => heatmapData.value.length > 0)

const maxValue = computed(() => {
  if (!heatmapData.value) return 0
  if (heatmapSettings.value.customValueRange) return heatmapSettings.value.customValueRange[1]
  return Math.max(...heatmapData.value.map((item) => item.value))
})

const minValue = computed(() => {
  if (!heatmapData.value) return 0
  if (heatmapSettings.value.customValueRange) return heatmapSettings.value.customValueRange[0]
  return Math.min(...heatmapData.value.map((item) => item.value))
})

const colorScale = computed<Scale>(() =>
  // chroma.scale(['074D65', 'FFE663']).domain([minValue.value, maxValue.value])
  chroma
    .scale([
      heatmapSettings.value.colors?.from || colorsForHeatmap.from,
      heatmapSettings.value.colors?.to || colorsForHeatmap.to,
    ])
    .domain([minValue.value, maxValue.value])
)

const styleSteps = 14

const styleArray = computed(() => {
  const styles = []
  if (!colorScale.value) return
  for (
    let i = minValue.value;
    i <= maxValue.value;
    i += (maxValue.value - minValue.value) / styleSteps
  ) {
    const strokeWidth = 1
    const color = colorScale.value(i) as unknown as Color

    const vectorStyle = new Style({
      fill: new Fill({
        color: color.alpha(0.9).hex(),
      }),
      stroke: new Stroke({
        width: strokeWidth,
        color: color.darken(0.4).hex(),
      }),
    })

    styles.push(vectorStyle)
  }

  return styles
})

function getClosestStyle(value: number) {
  if (styleArray.value) {
    const stepSize = (maxValue.value - minValue.value) / (styleSteps - 1)
    const styleIndex = Math.floor((value - minValue.value) / stepSize)
    return styleArray.value[styleIndex]
  }
}

function styleParser(feature: Feature<Geometry>): Style | undefined {
  const heatmapValue = feature.get('heatmapValue')
  return getClosestStyle(heatmapValue)
}
const featuresAreReady = ref(false)
const features = ref<Feature<Geometry>[]>([])

const { features: municipalityFeatures } = useGeojsonDataFetch('municipalities', {
  generalStyle: vectorStyle,
})

watch(municipalityFeatures, () => {
  parseFeatures()
})

const { features: countyFeatures } = useGeojsonDataFetch('counties', {
  generalStyle: vectorStyle,
})
watch(countyFeatures, () => {
  parseFeatures()
})

if (municipalityFeatures.value.length && countyFeatures.value.length) {
  parseFeatures()
}
function parseFeatures() {
  if (!municipalityFeatures.value.length || !countyFeatures.value.length) return

  features.value =
    heatmapSettings.value.areaType === 'municipality'
      ? cloneDeep(municipalityFeatures.value)
      : cloneDeep(countyFeatures.value)
  if (!heatmapDataIsReady.value) return

  features.value.forEach((feature) => {
    const idKey =
      heatmapSettings.value.areaType === 'municipality' ? 'municipality_code' : 'county_code'
    const id = feature.get(idKey)
    const heatmapDataItem = heatmapData.value.find((item) => item.id === id)
    const heatmapValue = heatmapDataItem?.value
    const color = heatmapValue !== undefined && getClosestStyle(heatmapValue)?.getFill()?.getColor()
    const nameKey =
      heatmapSettings.value.areaType === 'municipality' ? 'municipality_name' : 'county_name'
    feature.set('label', feature.get(nameKey))
    feature.set('heatmapValue', heatmapValue)
    feature.set('dataValue', heatmapDataItem?.displayValue || heatmapValue)
    feature.set('suffix', heatmapSettings.value?.dataSuffix || '')
    feature.set('color', color)
    feature.set('overlayType', 'mouse')
    feature.setStyle(styleParser(feature as Feature<Geometry>))

    feature.changed()
  })
  featuresAreReady.value = true
}

const layerControl = inject<{ name: string; dataLegendLabel: string; visible: Ref<boolean> }>(
  'layerControl'
)

const layerLegends = inject<{
  registerLegend: (legend: LayerLegend) => void
}>('layerLegends')

function updateLayerLegend() {
  if (!layerLegends) return
  layerLegends.registerLegend({
    name: (layerControl && layerControl.name) || 'Värmekarta',
    dataLegendLabel: layerControl && layerControl.dataLegendLabel,
    identifier: 'area-heat-map',
    type: 'gradient',
    visible: (layerControl && layerControl.visible) || ref(false),
    gradientData: {
      gradient: colorScale.value,
      min: minValue.value,
      max: maxValue.value,
      dataSuffix: heatmapSettings.value?.dataSuffix || '',
    },
  })
}

watch(heatmapSettings, () => {
  if (!heatmapSettings.value) return
  parseFeatures()
})

watch(
  heatmapData,
  () => {
    if (!heatmapData.value) return
    parseFeatures()
  },
  { immediate: true }
)

watch(colorScale, () => {
  updateLayerLegend()
})

onMounted(() => {
  updateLayerLegend()
})
</script>

<template>
  <BaseLayerVector v-if="featuresAreReady" :features="(features as Feature<Geometry>[])" />
</template>
